/**
 * KB-META
 * Purpose: Ai assistant chatbot component
 * Role: ui.component
 * EntryPoints: constructor(), if(), catch(), switch()
 * Inputs: private messageService:MessageService; private cdr:ChangeDetectorRef; private router:Router; private genusAIService:GenusAIService; private userService:UserService; private knowledgeService:AssistantKnowledgeService; this.selectedPages.length === 0:unknown; error:unknown; category:unknown
 * Outputs: 
 * Validation: 
 * Dependencies: MessageService, ChangeDetectorRef, Router, GenusAIService, UserService, AssistantKnowledgeService
 * Contracts: 
 * Auth&Privacy: auth:none; hipaa:none; pii:none
 * Observability: 
 * Router: shard=partner-portal-cura; H2=ai-assistant-chatbot/ai-assistant-chatbot.component; tags=ui
 * Owner: ; LastReviewed: 2025-08-25
 */
import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, NgZone } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DialogModule } from 'primeng/dialog';
import { ButtonModule } from 'primeng/button';
import { DropdownModule } from 'primeng/dropdown';
import { TextareaModule } from 'primeng/textarea';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { DividerModule } from 'primeng/divider';
import { CardModule } from 'primeng/card';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { TableModule } from 'primeng/table';
import { TabViewModule } from 'primeng/tabview';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ENV as environment } from '../../../config/environment';

// Import AI-related interfaces and services
import { GenusAImessage } from '../../../models/log.interface';
import { MarkdownPipe } from '../../../pipes/marked.pipe';

// Payload History Interfaces for debugging
export interface PayloadHistory {
  id: string;
  sessionId: string;
  timestamp: Date;
  request: {
    model: string;
    role: string;
    roleLabel: string;
    userInput: string;
    messageHistory: any[];
    parameters: any;
    apiEndpoint: string;
    modelCase: number;
  };
  response: {
    content: string;
    status: 'success' | 'error' | 'pending';
    responseTime: number;
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    errorDetails?: string;
  };
  metadata: {
    route: string;
    sessionDuration: number;
    messageCount: number;
    userAgent: string;
  };
}

export interface PayloadSession {
  sessionId: string;
  roleLabel: string;
  role: string;
  route: string;
  startTime: Date;
  endTime?: Date;
  messageCount: number;
  totalTokens: number;
  payloads: PayloadHistory[];
}

import systemAdvisorRole from './roles/system_advisor.json';
import systemLevelAssistantRole from './roles/system_level_assistant.json';
import contentAdvisorRole from './roles/content_advisor.json';
import onboardingConsultantRole from './roles/onboarding_consultant.json';
import tileEditorAssistantRole from './roles/tile_editor_assistant.json';
import careManagerAssistantRole from './roles/care_manager_assistant.json';
import analyticsAssistantRole from './roles/analytics_assistant.json';
import pathwayAssistantRole from './roles/pathway_assistant.json';
import settingsAssistantRole from './roles/settings_assistant.json';
import aiInnovatorRole from './roles/ai_innovator.json';
import { GenusAIService } from '../../../services/genus-ai.service';
import { UserService } from '../../../services/user.service';
import { UserDetails } from '../../../models/user.model';
import { AssistantKnowledgeService, SelectablePage, PageKnowledge } from './services/assistant-knowledge.service';
import { CHAT_MODELS, IMAGE_MODELS, getDefaultChatModel, getDefaultImageModel, ChatModelConfig, ImageModelConfig } from '../../../config/ai-models.config';

// Removed OpenAI direct API calls - all AI interactions now go through Genus backend

export interface AIRole {
  label: string;
  value: string;
  systemInstruction: string;
  description: string;
  icon: string;
  color: string;
  isContextual?: boolean; // Flag to identify context-specific roles
  routes?: string[]; // Routes where this role is most relevant
  config?: {
    useVectorStore?: boolean;
    defaultModel?: string;
    showSourceCitations?: boolean;
  };
}

// Genus Pilot Assistant Role - now uses same backend as other roles
const genusPilotAssistantRole: AIRole = {
  label: 'Genus Pilot Assistant',
  value: 'genus_pilot_assistant',
  systemInstruction: 'You are the Genus Pilot Assistant with comprehensive knowledge of the Genus platform. Provide detailed, accurate guidance to help users navigate and utilize the platform effectively. Focus on practical solutions and step-by-step guidance.',
  description: 'Comprehensive AI assistant for Genus platform guidance and support.',
  icon: 'pi-send',
  color: '#6366F1',
  isContextual: true,
  routes: ['/pilot', '/demo', '/testing']
};

export interface ChatMessage extends GenusAImessage {
  id: string;
  timestamp: Date;
  isUser: boolean;
  isTyping?: boolean;
  pairedMessageId?: string; // For linking user questions with AI responses
}

export interface QuestionSuggestion {
  id: string;
  question: string;
  context: string;
  category: string;
  roleId: string;
  timestamp: Date;
}

@Component({
  selector: 'app-ai-assistant-chatbot',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    DialogModule,
    ButtonModule,
    DropdownModule,
    TextareaModule,
    ScrollPanelModule,
    ProgressSpinnerModule,
    ToastModule,
    DividerModule,
    CardModule,
    TagModule,
    TooltipModule,
    TableModule,
    TabViewModule,
    MarkdownPipe
  ],
  providers: [MessageService],
  templateUrl: './ai-assistant-chatbot.component.html',
  styleUrls: ['./ai-assistant-chatbot.component.scss']
})
export class AIAssistantChatbotComponent implements OnInit, OnDestroy {
  @ViewChild('scrollPanel') scrollPanel: any;

  visible: boolean = false;
  selectedRole: AIRole | null = null;
  currentMessage: string = '';
  messages: ChatMessage[] = [];
  isLoading: boolean = false;
  isTyping: boolean = false;
  currentRoute: string = '';
  isRoleInfoVisible: boolean = false;
  roleInfoContent: string = '';
  roleInfoPath: string = '';

  // Session Management
  sessionStartTime: Date = new Date();
  sessionMessageCount: number = 0;
  currentSessionId: string = '';
  sessionInfo: string = '0 messages • 0m • Case 3'; // Add cached session info
  // Vector thread id for System Level Assistant conversations
  currentVectorThreadId: string | null = null;

  // Model Selection - config-driven
  selectedChatModel: ChatModelConfig = getDefaultChatModel();
  selectedModel: string = this.selectedChatModel.id;
  selectedModelCase: number = this.selectedChatModel.backendCase;
  // Playground-style prompt options
  playgroundSettings: any = {
    textFormat: 'text',          // 'text' | 'json'
    reasoningEffort: 'medium',   // 'low' | 'medium' | 'high'
    verbosity: 'medium',         // 'low' | 'medium' | 'high'
    summary: 'auto'              // 'auto' | 'always' | 'never'
  };
  // Dynamic capability flags for selected model
  private modelCapabilitiesById: Record<string, { options: { json: boolean; reasoning: boolean; verbosity: boolean; summary: boolean } }> = {};
  currentCaps = { json: true, reasoning: false, verbosity: true, summary: true };
  availableModels: { label: string; value: string; case: number; provider: string }[] = CHAT_MODELS.map(model => ({
    label: model.name,
    value: model.id,
    case: model.backendCase,
    provider: model.provider
  }));

  // Image Generation Properties
  showImageGenPanel: boolean = false;
  imagePrompt: string = '';
  selectedImageModel: ImageModelConfig = getDefaultImageModel();
  selectedImageProvider: 'openai' | 'gemini' = this.selectedImageModel.provider;
  selectedAspectRatio: string = '16:9';
  isGeneratingImage: boolean = false;
  generatedImageUrl: string = '';
  generatedImageData: any = null;
  availableImageModels = IMAGE_MODELS;
  availableAspectRatios = [
    { label: 'Square (1:1)', value: '1:1' },
    { label: 'Landscape (16:9)', value: '16:9' },
    { label: 'Portrait (9:16)', value: '9:16' }
  ];

  // Payload History Management for debugging
  payloadHistory: PayloadHistory[] = [];
  payloadSessions: PayloadSession[] = [];
  isPayloadHistoryVisible: boolean = false;
  isPayloadDetailsVisible: boolean = false;
  selectedPayloadDetails: PayloadHistory | null = null;

  private destroy$ = new Subject<void>();
  private sessionInfoTimer: any; // Add timer for session info updates

  // All available roles including contextual ones
  availableRoles: AIRole[] = [
    systemAdvisorRole,
    systemLevelAssistantRole,
    contentAdvisorRole,
    onboardingConsultantRole,
    tileEditorAssistantRole,
    careManagerAssistantRole,
    analyticsAssistantRole,
    pathwayAssistantRole,
    settingsAssistantRole,
    aiInnovatorRole,
    genusPilotAssistantRole
  ];

  // User details for API calls
  private userDetails: UserDetails | null = null;
  
  // Question Boost Feature
  showQuestionBoostModal: boolean = false;
  activeBoostTab: 'generate' | 'history' = 'generate';
  isGeneratingQuestions: boolean = false;
  generationStep: number = 0;
  currentQuestionSuggestions: QuestionSuggestion[] = [];
  questionHistory: QuestionSuggestion[] = [];
  private readonly maxHistoryItems = 20;

  // Platform Knowledge Selection Properties
  showPageSelectionModal: boolean = false;
  availablePages: SelectablePage[] = [];
  selectedPages: SelectablePage[] = [];
  pagesByCategory: { [category: string]: SelectablePage[] } = {};
  maxSelectedPages = 5;
  isLoadingPages = false;
  searchTerm = '';
  filteredPages: SelectablePage[] = [];
  recommendedPages: SelectablePage[] = [];
  knowledgeStats = {
    totalPages: 0,
    totalCategories: 0,
    lastUpdated: '',
    averageComplexity: ''
  };
  originalInnovatorInstruction = '';
  isContextUpdating = false;
  
  constructor(
    private messageService: MessageService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private genusAIService: GenusAIService,
    private userService: UserService,
    private knowledgeService: AssistantKnowledgeService
  ) {
    // Initialize user details
    this.userDetails = this.userService.initializeUserDetails();
  }

  ngOnInit(): void {
    // Get current route
    this.currentRoute = this.router.url;
    
    // Set contextual role based on current route or default to System Advisor
    this.selectedRole = this.getContextualRole() || this.availableRoles[0];

    // Ensure selectedModelCase is properly set on initialization
    const defaultModelObj = this.availableModels.find(model => model.value === this.selectedModel);
    if (defaultModelObj) {
      this.selectedModelCase = defaultModelObj.case;
      console.log(`Initialized with model: ${this.selectedModel} (Case ${this.selectedModelCase})`);
    }

    // Load payload history
    this.loadPayloadHistory();
    
    // Load question history
    this.loadQuestionHistory();

    // Load model capability metadata once
    this.loadModelCapabilities();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.clearSessionInfoTimer(); // Clear the timer
  }


  private clearSessionInfoTimer(): void {
    if (this.sessionInfoTimer) {
      clearInterval(this.sessionInfoTimer);
      this.sessionInfoTimer = null;
    }
  }

  private updateSessionInfo(): void {
    const duration = Date.now() - this.sessionStartTime.getTime();
    const minutes = Math.floor(duration / 60000);
    const hours = Math.floor(minutes / 60);
    
    let timeString = '';
    if (hours > 0) {
      timeString = `${hours}h ${minutes % 60}m`;
    } else {
      timeString = `${minutes}m`;
    }
    
    this.sessionInfo = `${this.sessionMessageCount} messages • ${timeString} • Case ${this.selectedModelCase}`;
  }

  openChatbot(): void {
    // Update current route and contextual role when opening
    this.currentRoute = this.router.url;
    const contextualRole = this.getContextualRole();
    
    // Only change role if we found a contextual one and messages are empty (fresh start)
    if (contextualRole && this.messages.length === 0) {
      this.selectedRole = contextualRole;
    }
    
    this.visible = true;
    if (this.messages.length === 0) {
      this.startNewSession();
      this.addWelcomeMessage();
    }
    
    // Use markForCheck instead of detectChanges to be safer
    this.cdr.markForCheck();
  }

  closeChatbot(): void {
    this.visible = false;
  }

  // Session Management
  private startNewSession(): void {
    this.currentSessionId = this.generateId();
    this.sessionStartTime = new Date();
    this.sessionMessageCount = 0;
    this.updateSessionInfo(); // Update immediately when starting new session
  }

  addWelcomeMessage(): void {
    if (this.selectedRole) {
      const isContextual = this.selectedRole.isContextual;
      const contextMessage = isContextual ? 
        `\n\n*I've automatically selected this role based on your current page to provide the most relevant assistance.*` : '';
      
      let welcomeContent: string;
      
      // Special welcome message for AI Innovator
      if (this.selectedRole.value === 'ai_innovator') {
        welcomeContent = `Hello! I'm your **${this.selectedRole.label}**. 

**Let's innovate! Share your idea for the ecosystem - I'll help you refine it, then we explore simulated focus group and workflows.**

${this.selectedRole.description}${contextMessage}`;
      } else {
        welcomeContent = `Hello! I'm your **${this.selectedRole.label}**. ${this.selectedRole.description}${contextMessage}

How can I assist you today?`;
      }
      
      const welcomeMessage: ChatMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: welcomeContent,
        timestamp: new Date(),
        isUser: false
      };
      this.messages.push(welcomeMessage);
    }
  }

  async sendMessage(): Promise<void> {
    if (!this.currentMessage.trim() || !this.selectedRole) return;

    const userMessageId = this.generateId();
    const userMessage: ChatMessage = {
      id: userMessageId,
      role: 'user',
      content: this.currentMessage.trim(),
      timestamp: new Date(),
      isUser: true
    };

    this.messages.push(userMessage);
    const userInput = this.currentMessage.trim();
    this.currentMessage = '';
    
    this.scrollToBottom();
    await this.getAIResponse(userInput, userMessageId);
    this.updateSessionInfo(); // Update session info after message sent
  }

  private async getAIResponse(userInput: string, userMessageId: string): Promise<void> {
    this.isLoading = true;
    this.isTyping = true;

    // Add typing indicator
    const typingMessage: ChatMessage = {
      id: 'typing',
      role: 'assistant',
      content: '',
      timestamp: new Date(),
      isUser: false,
      isTyping: true
    };
    this.messages.push(typingMessage);
    this.scrollToBottom();

    try {
      const response = await this.callGenusAI(userInput);
      
      // Remove typing indicator
      this.messages = this.messages.filter(m => m.id !== 'typing');

      const aiMessage: ChatMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: response,
        timestamp: new Date(),
        isUser: false,
        pairedMessageId: userMessageId // Link response to the user's question
      };

      this.messages.push(aiMessage);
      this.sessionMessageCount++;
      this.updateSessionInfo(); // Update session info after response received
      this.scrollToBottom();

    } catch (error) {
      console.error('Error getting AI response:', error);
      
      // Remove typing indicator
      this.messages = this.messages.filter(m => m.id !== 'typing');

      // Show detailed error message for debugging
      const errorDetails = error instanceof Error ? error.message : 'Unknown error occurred';
      const detailedErrorContent = `**⚠️ Error Details:**

\`\`\`
${errorDetails}
\`\`\`

**Debug Information:**
- **Role**: ${this.selectedRole?.label || 'Unknown'}
- **Model**: ${this.selectedModel}
- **Timestamp**: ${new Date().toISOString()}
- **Session ID**: ${this.currentSessionId}
- **Route**: ${this.currentRoute}

Please check the payload history (database icon) for detailed API call information.`;

      const errorMessage: ChatMessage = {
        id: this.generateId(),
        role: 'assistant',
        content: detailedErrorContent,
        timestamp: new Date(),
        isUser: false,
        pairedMessageId: userMessageId
      };

      this.messages.push(errorMessage);

      this.messageService.add({
        severity: 'error',
        summary: 'AI Response Error',
        detail: errorDetails
      });
    } finally {
      this.isLoading = false;
      this.isTyping = false;
      this.cdr.detectChanges();
    }
  }

  private async callGenusAI(userInput: string): Promise<string> {
    // Route to vector assistant if role is configured for vector store
    if (this.selectedRole?.config?.useVectorStore) {
      const { answer, sources } = await this.callVectorAssistant(userInput);
      // Attach sources as a synthetic message after the AI answer, if enabled
      if (this.selectedRole?.config?.showSourceCitations && Array.isArray(sources) && sources.length > 0) {
        const sourcesList = sources
          .slice(0, 5)
          .map((s: any) => `- ${s.fileName}${s.quote ? ` — “${(s.quote || '').slice(0, 160)}${(s.quote || '').length > 160 ? '…' : ''}”` : ''}`)
          .join('\n');
        const meta = `\n\n<details><summary><strong>Sources</strong></summary>\n\n${sourcesList}\n\n</details>`;
        return answer + meta;
      }
      return answer;
    }
    const role = this.selectedRole!.value;
    const startTime = Date.now();
    const payloadId = this.generateId();
    
    try {
      // Track the request payload
      this.trackPayloadStart(payloadId, userInput, this.genusAIService.encrpytApiUrl);
      
      // Build conversation context with system instruction
      const systemInstruction = this.selectedRole?.systemInstruction || '';
      const conversationHistory = this.buildConversationHistory();
      
      // Create proper message format - EXACTLY like working genus-tiles components
      const messages = [
        {
          role: 'system',
          content: systemInstruction
        },
        ...conversationHistory,
        {
          role: 'user', 
          content: userInput
        }
      ];
      
      console.log('🤖 Calling Genus AI API with payload:', {
        model: this.selectedModelCase,
        modelName: this.selectedModel,
        role: this.selectedRole?.label,
        userInput: userInput.substring(0, 100) + '...',
        sessionId: this.currentSessionId,
        messagesCount: messages.length
      });
      
      // Use the EXACT same pattern as working genus-tiles components
      const apiPayload = {
        username: this.userDetails?.preferred_username || 'anonymous',
        partnerid: Number(this.userDetails?.custom_partner_code || '0'),
        model: Number(this.selectedModelCase),
        messages: await this.genusAIService.encryptPayload(JSON.stringify(messages)),
        xRefID: this.currentSessionId,
        ui: { playground: this.playgroundSettings },
        stream: true
      };
      
      // Make API call using dedicated AI Assistant endpoint
      const response = await this.genusAIService.assistantChatApi(apiPayload).toPromise();
      
      const responseTime = Date.now() - startTime;
      console.log('✅ Genus AI Response received in', responseTime, 'ms');
      
      // Parse response - genus-tiles components expect direct decrypted response
      let aiResponse: string;
      try {
        // First try to decrypt the response
        const decryptedResponse = this.genusAIService.decryptPayload(response);
        
        // Try to parse as JSON first
        try {
          const parsedResponse = JSON.parse(decryptedResponse);
          
          // Extract the AI response based on OpenAI API structure
          if (parsedResponse.choices && parsedResponse.choices[0] && parsedResponse.choices[0].message) {
            aiResponse = parsedResponse.choices[0].message.content;
          } else if (parsedResponse.content) {
            aiResponse = parsedResponse.content;
          } else if (typeof parsedResponse === 'string') {
            aiResponse = parsedResponse;
          } else {
            aiResponse = JSON.stringify(parsedResponse);
          }
        } catch (parseError) {
          // If it's not JSON, use the decrypted response directly (like genus-tiles)
          aiResponse = decryptedResponse || 'No response received from AI service';
        }
      } catch (decryptError) {
        console.warn('Could not decrypt AI response, using raw response:', decryptError);
        aiResponse = response || 'No response received from AI service';
      }
      
      // Track successful response
      this.trackPayloadComplete(
        payloadId, 
        aiResponse, 
        'success', 
        responseTime,
        0, // promptTokens - not available in this API
        0, // completionTokens - not available in this API  
        0  // totalTokens - not available in this API
      );
      
      return aiResponse;
      
    } catch (error) {
      const responseTime = Date.now() - startTime;
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      
      console.error('❌ Genus AI API Error:', error);
      
      // Track failed response
      this.trackPayloadComplete(payloadId, '', 'error', responseTime, 0, 0, 0, errorMsg);
      
      throw new Error(`Genus AI API Error: ${errorMsg}`);
    }
  }

  private async callVectorAssistant(userInput: string): Promise<{ answer: string; sources: any[] }> {
    const startTime = Date.now();
    const payloadId = this.generateId();
    try {
      this.trackPayloadStart(payloadId, userInput, `${this.genusAIService.encrpytApiUrl}api/vector/chat`);
      const body: any = {
        question: userInput,
        // Prefer user's current selection; fallback to role default
        model: this.selectedModel || this.selectedRole?.config?.defaultModel,
        threadId: this.currentVectorThreadId,
      };
      // Include vectorStoreId if present in role config
      const anyRole: any = this.selectedRole as any;
      if (anyRole?.config?.vectorStoreId) {
        body.vectorStoreId = anyRole.config.vectorStoreId;
      }
      // vector store id may be provided by backend env; keep optional
      const response: any = await this.genusAIService.postVectorChat(body).toPromise();
      if (response?.threadId) {
        this.currentVectorThreadId = response.threadId;
      }
      const answer = response?.answer || '';
      const sources = Array.isArray(response?.sources) ? response.sources : [];
      const responseTime = Date.now() - startTime;
      this.trackPayloadComplete(payloadId, answer, 'success', responseTime, 0, 0, 0);
      return { answer, sources };
    } catch (error: any) {
      const responseTime = Date.now() - startTime;
      this.trackPayloadComplete(payloadId, '', 'error', responseTime, 0, 0, 0, error?.message || 'Vector chat error');
      throw error;
    }
  }
  
  /**
   * Build conversation history in proper message format for the API
   */
  private buildConversationHistory(): Array<{role: string, content: string}> {
    // Get conversation history (excluding typing indicators)
    return this.messages
      .filter(msg => !msg.isTyping)
      .map(msg => ({
        role: msg.isUser ? 'user' : 'assistant',
        content: msg.content
      }));
  }

  // Removed OpenAI Assistants API methods - all AI calls now go through Genus backend

  onKeyPress(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.sendMessage();
    }
  }

  exportChat(): void {
    const chatData = {
      role: this.selectedRole?.label,
      timestamp: new Date().toISOString(),
      messages: this.messages.filter(m => !m.isTyping)
    };

    const blob = new Blob([JSON.stringify(chatData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-chat-${this.selectedRole?.value}-${new Date().getTime()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    this.messageService.add({
      severity: 'success',
      summary: 'Chat Exported',
      detail: 'Conversation has been exported successfully'
    });
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      if (this.scrollPanel) {
        this.scrollPanel.scrollTop(this.scrollPanel.contentHeight);
      }
    }, 100);
  }

  private generateId(): string {
    return Date.now().toString(36) + Math.random().toString(36).substr(2);
  }

  getRoleIcon(role: AIRole): string {
    return role.icon;
  }

  getRoleColor(role: AIRole): string {
    return role.color;
  }

  formatTimestamp(timestamp: Date): string {
    return timestamp.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }

  getMessageClasses(message: ChatMessage): string {
    return `message ${message.isUser ? 'user-message' : 'ai-message'}`;
  }

  trackMessage(index: number, message: ChatMessage): string {
    return message.id;
  }

  showRoleInfo(): void {
    if (this.selectedRole) {
      this.roleInfoPath = `src/app/views/common/ai-assistant-chatbot/roles/${this.selectedRole.value}.json`;
      this.roleInfoContent = JSON.stringify(this.selectedRole, null, 2);
      this.isRoleInfoVisible = true;
    }
  }

  private getContextualRole(): AIRole | null {
    // Find a role that matches the current route
    return this.availableRoles.find(role => 
      role.isContextual && 
      role.routes?.some(route => 
        this.currentRoute.includes(route) || 
        this.currentRoute.endsWith(route) ||
        // Additional tile-related route detection
        (role.value === 'tile_editor_assistant' && (
          this.currentRoute.includes('/tile-editor') ||
          this.currentRoute.includes('/manage-genus-tile') ||
          this.currentRoute.includes('/add-edit-contact-tile') ||
          this.currentRoute.includes('/content/tile-editor') ||
          this.currentRoute.includes('genus-tile') ||
          this.currentRoute.includes('/tile/')
        ))
      )
    ) || null;
  }

  // Get roles to display in dropdown (contextual role first if available, then general roles)
  getDisplayRoles(): AIRole[] {
    const contextualRole = this.getContextualRole();
    const generalRoles = this.availableRoles.filter(role => !role.isContextual);
    const otherContextualRoles = this.availableRoles.filter(role => 
      role.isContextual && role !== contextualRole
    );

    if (contextualRole) {
      return [contextualRole, ...generalRoles, ...otherContextualRoles];
    }
    
    return [...generalRoles, ...otherContextualRoles];
  }

  onRoleChange(): void {
    // Clear previous messages when role changes
    this.messages = [];
    this.addWelcomeMessage();
    this.scrollToBottom();
  }

  onModelChange(): void {
    // Update the selectedModelCase based on the selected model to align with Genus AI backend
    const selectedModelObj = this.availableModels.find(model => model.value === this.selectedModel);
    if (selectedModelObj) {
      this.selectedModelCase = selectedModelObj.case;
      console.log(`Model changed to: ${this.selectedModel}`);
      this.updateCurrentCaps();
      
      this.messageService.add({
        severity: 'info',
        summary: 'Model Updated',
        detail: `Switched to ${selectedModelObj.label}`
      });
    }
  }

  private loadModelCapabilities(): void {
    this.genusAIService.getModelsMeta().subscribe({
      next: (resp: any) => {
        const arr = Array.isArray(resp?.models) ? resp.models : [];
        this.modelCapabilitiesById = {};
        arr.forEach((m: any) => {
          if (m?.id && m?.options) this.modelCapabilitiesById[m.id] = { options: {
            json: !!m.options.json,
            reasoning: !!m.options.reasoning,
            verbosity: m.options.verbosity !== false,
            summary: m.options.summary !== false
          }};
        });
        this.updateCurrentCaps();
      },
      error: _e => {
        // Fallback caps if endpoint unavailable
        this.modelCapabilitiesById = {
          'gpt-4o-mini': { options: { json: true, reasoning: false, verbosity: true, summary: true }},
          'gpt-4o': { options: { json: true, reasoning: false, verbosity: true, summary: true }},
          'gpt-5-instant': { options: { json: true, reasoning: false, verbosity: true, summary: true }},
          'gpt-5-thinking': { options: { json: true, reasoning: true, verbosity: true, summary: true }},
          'gpt-5-pro': { options: { json: true, reasoning: false, verbosity: true, summary: true }},
          'gemini-2.0-flash-exp': { options: { json: true, reasoning: false, verbosity: true, summary: true }},
        } as any;
        this.updateCurrentCaps();
      }
    })
  }

  private updateCurrentCaps(): void {
    const caps = this.modelCapabilitiesById[this.selectedModel]?.options;
    this.currentCaps = {
      json: caps ? !!caps.json : true,
      reasoning: caps ? !!caps.reasoning : false,
      verbosity: caps ? caps.verbosity !== false : true,
      summary: caps ? caps.summary !== false : true
    };
    // If current model doesn’t support reasoning, normalize the setting
    if (!this.currentCaps.reasoning) {
      this.playgroundSettings.reasoningEffort = 'medium';
    }
    if (!this.currentCaps.json && this.playgroundSettings.textFormat === 'json') {
      this.playgroundSettings.textFormat = 'text';
    }
  }

  // Message Management Methods
  deleteMessagePair(messageId: string): void {
    // Find the user message and its paired AI response
    const userMessageIndex = this.messages.findIndex(m => m.id === messageId);
    if (userMessageIndex === -1) return;

    const userMessage = this.messages[userMessageIndex];
    
    // If it's a user message, find its paired AI response
    if (userMessage.isUser) {
      const aiResponseIndex = this.messages.findIndex(m => m.pairedMessageId === messageId);
      
      // Remove both messages
      if (aiResponseIndex !== -1) {
        // Remove in reverse order to maintain correct indices
        if (aiResponseIndex > userMessageIndex) {
          this.messages.splice(aiResponseIndex, 1);
          this.messages.splice(userMessageIndex, 1);
        } else {
          this.messages.splice(userMessageIndex, 1);
          this.messages.splice(aiResponseIndex, 1);
        }
      } else {
        // Only remove user message if no AI response found
        this.messages.splice(userMessageIndex, 1);
      }
    }
    // If it's an AI message, find its paired user message
    else if (userMessage.pairedMessageId) {
      const userQuestionIndex = this.messages.findIndex(m => m.id === userMessage.pairedMessageId);
      
      // Remove both messages
      if (userQuestionIndex !== -1) {
        // Remove in reverse order to maintain correct indices
        if (userMessageIndex > userQuestionIndex) {
          this.messages.splice(userMessageIndex, 1);
          this.messages.splice(userQuestionIndex, 1);
        } else {
          this.messages.splice(userQuestionIndex, 1);
          this.messages.splice(userMessageIndex, 1);
        }
      } else {
        // Only remove AI message if no user question found
        this.messages.splice(userMessageIndex, 1);
      }
    }

    this.messageService.add({
      severity: 'info',
      summary: 'Message Deleted',
      detail: 'Question and answer pair removed from conversation'
    });
  }

  clearSessionHistory(): void {
    this.messages = [];
    this.sessionMessageCount = 0;
    this.sessionStartTime = new Date();
    this.updateSessionInfo(); // Update session info immediately
    this.addWelcomeMessage();
    
    this.messageService.add({
      severity: 'info',
      summary: 'Session Cleared',
      detail: 'Conversation history has been cleared'
    });
  }

  getSessionInfo(): string {
    return this.sessionInfo; // Return cached value instead of calculating
  }

  // Payload History Management
  private trackPayloadStart(payloadId: string, userInput: string, apiEndpoint: string): void {
    const messageHistory = this.messages
      .filter(m => !m.isTyping)
      .map(m => ({
        role: m.isUser ? 'user' : 'assistant',
        content: m.content
      }));

    if (this.selectedRole) {
      messageHistory.unshift({
        role: 'system',
        content: this.selectedRole.systemInstruction
      });
    }

    const payload: PayloadHistory = {
      id: payloadId,
      sessionId: this.currentSessionId,
      timestamp: new Date(),
      request: {
        model: this.selectedModel,
        role: this.selectedRole?.value || 'unknown',
        roleLabel: this.selectedRole?.label || 'Unknown',
        userInput,
        messageHistory,
        parameters: {
          temperature: 0.7,
          max_tokens: 4000,
          top_p: 1,
          frequency_penalty: 0,
          presence_penalty: 0,
          modelCase: this.selectedModelCase,
          playground: { ...this.playgroundSettings }
        },
        apiEndpoint,
        modelCase: this.selectedModelCase
      },
      response: {
        content: '',
        status: 'pending',
        responseTime: 0,
        promptTokens: 0,
        completionTokens: 0,
        totalTokens: 0
      },
      metadata: {
        route: this.currentRoute,
        sessionDuration: Date.now() - this.sessionStartTime.getTime(),
        messageCount: this.sessionMessageCount,
        userAgent: navigator.userAgent
      }
    };

    this.payloadHistory.unshift(payload);
    this.updatePayloadSession(payload);
    this.savePayloadHistory();
  }

  private trackPayloadComplete(
    payloadId: string, 
    content: string, 
    status: 'success' | 'error', 
    responseTime: number,
    promptTokens: number = 0,
    completionTokens: number = 0,
    totalTokens: number = 0,
    errorDetails?: string
  ): void {
    const payloadIndex = this.payloadHistory.findIndex(p => p.id === payloadId);
    if (payloadIndex !== -1) {
      this.payloadHistory[payloadIndex].response = {
        content,
        status,
        responseTime,
        promptTokens,
        completionTokens,
        totalTokens,
        errorDetails
      };
      
      this.updatePayloadSession(this.payloadHistory[payloadIndex]);
      this.savePayloadHistory();
    }
  }

  private updatePayloadSession(payload: PayloadHistory): void {
    let session = this.payloadSessions.find(s => s.sessionId === payload.sessionId);
    
    if (!session) {
      session = {
        sessionId: payload.sessionId,
        roleLabel: payload.request.roleLabel,
        role: payload.request.role,
        route: payload.metadata.route,
        startTime: this.sessionStartTime,
        messageCount: 0,
        totalTokens: 0,
        payloads: []
      };
      this.payloadSessions.unshift(session);
    }

    // Update session
    session.messageCount = payload.metadata.messageCount;
    session.totalTokens += payload.response.totalTokens;
    session.endTime = payload.timestamp;
    
    // Add payload to session if not already there
    if (!session.payloads.find(p => p.id === payload.id)) {
      session.payloads.push(payload);
    }

    // Keep only last 20 sessions
    this.payloadSessions = this.payloadSessions.slice(0, 20);
  }

  private savePayloadHistory(): void {
    try {
      if (environment.production) {
        return;
      }
      // Keep only last 50 payloads
      this.payloadHistory = this.payloadHistory.slice(0, 50);
      localStorage.setItem('ai-assistant-payload-history', JSON.stringify(this.payloadHistory));
      localStorage.setItem('ai-assistant-payload-sessions', JSON.stringify(this.payloadSessions));
    } catch (error) {
      console.warn('Failed to save payload history to localStorage:', error);
    }
  }

  private loadPayloadHistory(): void {
    try {
      if (environment.production) {
        this.payloadHistory = [];
        this.payloadSessions = [];
        return;
      }
      const historyData = localStorage.getItem('ai-assistant-payload-history');
      const sessionsData = localStorage.getItem('ai-assistant-payload-sessions');
      
      if (historyData) {
        this.payloadHistory = JSON.parse(historyData).map((p: any) => ({
          ...p,
          timestamp: new Date(p.timestamp)
        }));
      }
      
      if (sessionsData) {
        this.payloadSessions = JSON.parse(sessionsData).map((s: any) => ({
          ...s,
          startTime: new Date(s.startTime),
          endTime: s.endTime ? new Date(s.endTime) : undefined,
          payloads: s.payloads.map((p: any) => ({
            ...p,
            timestamp: new Date(p.timestamp)
          }))
        }));
      }
    } catch (error) {
      console.warn('Failed to load payload history from localStorage:', error);
      this.payloadHistory = [];
      this.payloadSessions = [];
    }
  }

  // Payload History UI Methods
  showPayloadHistory(): void {
    this.isPayloadHistoryVisible = true;
  }

  showPayloadDetails(payload: PayloadHistory): void {
    this.selectedPayloadDetails = payload;
    this.isPayloadDetailsVisible = true;
  }

  copyPayload(payload: PayloadHistory): void {
    navigator.clipboard.writeText(JSON.stringify(payload, null, 2)).then(() => {
      this.messageService.add({
        severity: 'success',
        summary: 'Copied',
        detail: 'Payload copied to clipboard'
      });
    }).catch(() => {
      this.messageService.add({
        severity: 'error',
        summary: 'Copy Failed',
        detail: 'Failed to copy payload to clipboard'
      });
    });
  }

  exportPayloadHistory(): void {
    const exportData = {
      sessions: this.payloadSessions,
      payloads: this.payloadHistory,
      exportedAt: new Date().toISOString()
    };

    const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ai-assistant-payload-history-${new Date().getTime()}.json`;
    a.click();
    URL.revokeObjectURL(url);

    this.messageService.add({
      severity: 'success',
      summary: 'Export Complete',
      detail: 'Payload history exported successfully'
    });
  }

  clearPayloadHistory(): void {
    this.payloadHistory = [];
    this.payloadSessions = [];
    if (!environment.production) {
      localStorage.removeItem('ai-assistant-payload-history');
      localStorage.removeItem('ai-assistant-payload-sessions');
    }
    
    this.messageService.add({
      severity: 'info',
      summary: 'History Cleared',
      detail: 'All payload history has been cleared'
    });
  }

  getPayloadStatusColor(status: string): string {
    switch (status) {
      case 'success': return '#10B981';
      case 'error': return '#EF4444';
      case 'pending': return '#F59E0B';
      default: return '#6B7280';
    }
  }

  formatDuration(milliseconds: number): string {
    const seconds = Math.floor(milliseconds / 1000);
    const minutes = Math.floor(seconds / 60);
    const hours = Math.floor(minutes / 60);
    
    if (hours > 0) {
      return `${hours}h ${minutes % 60}m ${seconds % 60}s`;
    } else if (minutes > 0) {
      return `${minutes}m ${seconds % 60}s`;
    } else {
      return `${seconds}s`;
    }
  }

  // Question Boost Modal Methods
  openQuestionBoostModal(): void {
    this.showQuestionBoostModal = true;
    this.activeBoostTab = 'generate';
    
         // Generate questions if none exist
     if (this.currentQuestionSuggestions.length === 0) {
       this.generateQuestions();
     }
  }

  closeQuestionBoostModal(): void {
    this.showQuestionBoostModal = false;
  }

  setActiveBoostTab(tab: 'generate' | 'history'): void {
    this.activeBoostTab = tab;
  }

  async generateQuestions(): Promise<void> {
    if (!this.selectedRole || this.isGeneratingQuestions) return;
    
    this.isGeneratingQuestions = true;
    
    try {
      // Generate contextual questions based on role and conversation history
      const suggestions = this.generateRoleBasedQuestions();
      
      // Add timestamp and IDs
      this.currentQuestionSuggestions = suggestions.map(suggestion => ({
        ...suggestion,
        id: this.generateId(),
        roleId: this.selectedRole!.value,
        timestamp: new Date()
      }));
      
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error('Error generating questions:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Question Generation Failed',
        detail: 'Failed to generate question suggestions. Please try again.',
        life: 3000
      });
    } finally {
      this.isGeneratingQuestions = false;
    }
  }

  async regenerateQuestions(): Promise<void> {
    this.currentQuestionSuggestions = [];
    await this.generateQuestions();
  }

  selectSuggestion(suggestion: QuestionSuggestion): void {
    // Set the suggestion as the current message
    this.currentMessage = suggestion.question;
    
    // Add to history if not already there
    this.addToQuestionHistory(suggestion);
    
    // Close the boost modal
    this.closeQuestionBoostModal();
    
    // Focus on the textarea
    setTimeout(() => {
      const textarea = document.querySelector('.message-input') as HTMLTextAreaElement;
      if (textarea) {
        textarea.focus();
        textarea.setSelectionRange(textarea.value.length, textarea.value.length);
      }
    }, 100);
    
    this.cdr.detectChanges();
  }

  // Method for selecting suggestion from modal
  selectSuggestionFromModal(suggestion: QuestionSuggestion): void {
    this.selectSuggestion(suggestion);
  }

  // Method for generating questions in modal with animation
  async generateQuestionsModal(): Promise<void> {
    if (!this.selectedRole || this.isGeneratingQuestions) return;
    
    this.isGeneratingQuestions = true;
    this.generationStep = 0;
    
    try {
      // Simulate step-by-step generation with animation
      this.generationStep = 1;
      await new Promise(resolve => setTimeout(resolve, 800));
      
      this.generationStep = 2;
      await new Promise(resolve => setTimeout(resolve, 800));
      
      this.generationStep = 3;
      await new Promise(resolve => setTimeout(resolve, 600));
      
      // Generate contextual questions based on role and conversation history
      const suggestions = this.generateRoleBasedQuestions();
      
      // Add timestamp and IDs
      this.currentQuestionSuggestions = suggestions.map(suggestion => ({
        ...suggestion,
        id: this.generateId(),
        roleId: this.selectedRole!.value,
        timestamp: new Date()
      }));
      
      this.cdr.detectChanges();
      
    } catch (error) {
      console.error('Error generating questions:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Question Generation Failed',
        detail: 'Failed to generate question suggestions. Please try again.',
        life: 3000
      });
    } finally {
      this.isGeneratingQuestions = false;
      this.generationStep = 0;
    }
  }

  // Format date for history display
  formatDate(date: Date): string {
    return new Intl.DateTimeFormat('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    }).format(date);
  }

  private addToQuestionHistory(suggestion: QuestionSuggestion): void {
    // Check if question already exists in history
    const existingIndex = this.questionHistory.findIndex(
      item => item.question === suggestion.question
    );
    
    if (existingIndex !== -1) {
      // Move to top if exists
      this.questionHistory.splice(existingIndex, 1);
    }
    
    // Add to top of history
    this.questionHistory.unshift({
      ...suggestion,
      timestamp: new Date()
    });
    
    // Limit history size
    if (this.questionHistory.length > this.maxHistoryItems) {
      this.questionHistory = this.questionHistory.slice(0, this.maxHistoryItems);
    }
    
    // Save to localStorage
    this.saveQuestionHistory();
  }

  private generateRoleBasedQuestions(): Omit<QuestionSuggestion, 'id' | 'roleId' | 'timestamp'>[] {
    if (!this.selectedRole) return [];
    
    const roleId = this.selectedRole.value;
    const hasConversation = this.messages.length > 1;
    const roleLabel = this.selectedRole.label;
    
    // Base questions for each role
    const roleQuestions: { [key: string]: Omit<QuestionSuggestion, 'id' | 'roleId' | 'timestamp'>[] } = {
      tile_editor_assistant: [
        {
          question: "What are the best practices for creating patient-friendly content with appropriate reading levels?",
          context: "Content creation guidance for patient education materials",
          category: "Content Strategy"
        },
        {
          question: "How do I optimize video content for mobile viewing and ensure accessibility compliance?",
          context: "Media optimization and accessibility best practices",
          category: "Media Guidelines"
        },
        {
          question: "What interactive elements work best for patient engagement in educational tiles?",
          context: "Interactive design patterns and engagement strategies",
          category: "Engagement"
        },
        {
          question: "How can I use AI chat features to enhance patient education experiences?",
          context: "AI integration strategies for patient communication",
          category: "AI Features"
        },
        {
          question: "What are the key fields I should focus on in the tile editor for maximum impact?",
          context: "Field prioritization and form completion guidance",
          category: "Field Guidance"
        }
      ],
      
      care_manager_assistant: [
        {
          question: "How do I identify and prioritize high-risk patients who need immediate attention?",
          context: "Risk stratification and patient triage workflows",
          category: "Risk Management"
        },
        {
          question: "What are the most effective communication strategies for engaging inactive patients?",
          context: "Patient engagement and communication best practices",
          category: "Patient Engagement"
        },
        {
          question: "How can I streamline my daily workflow to manage more patients efficiently?",
          context: "Workflow optimization and productivity enhancement",
          category: "Workflow"
        },
        {
          question: "What metrics should I track to measure patient outcomes and care effectiveness?",
          context: "Quality measurement and outcome tracking strategies",
          category: "Analytics"
        },
        {
          question: "How do I coordinate care transitions and handoffs between team members?",
          context: "Care coordination and team collaboration protocols",
          category: "Care Coordination"
        }
      ],
      
      ai_innovator: [
        {
          question: "I have an idea for improving patient engagement through gamification - how do we explore this?",
          context: "Innovation exploration for patient engagement solutions",
          category: "Innovation"
        },
        {
          question: "What would be the best approach to integrate voice assistants into the care workflow?",
          context: "Technology integration and workflow innovation",
          category: "Technology"
        },
        {
          question: "How could we use predictive analytics to identify patients at risk of readmission?",
          context: "AI and analytics applications in healthcare",
          category: "Predictive Analytics"
        },
        {
          question: "What ecosystem partnerships could enhance our platform's value proposition?",
          context: "Strategic partnerships and ecosystem development",
          category: "Partnerships"
        },
        {
          question: "How might we redesign the patient onboarding process to reduce drop-off rates?",
          context: "User experience optimization and process innovation",
          category: "UX Innovation"
        }
      ],
      
      system_advisor: [
        {
          question: "How do I configure user permissions to ensure proper access control across departments?",
          context: "User management and security configuration",
          category: "Security"
        },
        {
          question: "What are the system requirements for optimal performance with large patient volumes?",
          context: "Performance optimization and scalability planning",
          category: "Performance"
        },
        {
          question: "How can I set up automated backups and disaster recovery procedures?",
          context: "Data protection and business continuity planning",
          category: "Data Protection"
        },
        {
          question: "What integration options are available for connecting with existing hospital systems?",
          context: "System integration and interoperability planning",
          category: "Integration"
        },
        {
          question: "How do I troubleshoot common connectivity issues that users experience?",
          context: "Technical support and troubleshooting guidance",
          category: "Troubleshooting"
        }
      ],
      
      content_advisor: [
        {
          question: "How do I create culturally sensitive content for diverse patient populations?",
          context: "Cultural competency and inclusive content development",
          category: "Cultural Sensitivity"
        },
        {
          question: "What are the best practices for translating medical content for patient understanding?",
          context: "Health literacy and medical communication strategies",
          category: "Health Literacy"
        },
        {
          question: "How can I measure the effectiveness of educational content on patient outcomes?",
          context: "Content performance measurement and optimization",
          category: "Content Analytics"
        },
        {
          question: "What content formats work best for different learning styles and age groups?",
          context: "Audience-specific content strategy and format selection",
          category: "Content Format"
        },
        {
          question: "How do I ensure my content complies with healthcare regulations and privacy requirements?",
          context: "Regulatory compliance and content governance",
          category: "Compliance"
        }
      ]
    };
    
    // Get base questions for the current role
    let questions = roleQuestions[roleId] || roleQuestions['system_advisor'];
    
    // Add conversation-specific questions if there's history
    if (hasConversation) {
      const conversationQuestions = [
        {
          question: "Can you provide more specific examples related to what we just discussed?",
          context: "Follow-up question based on current conversation context",
          category: "Follow-up"
        },
        {
          question: "What are the potential challenges or risks I should consider with this approach?",
          context: "Risk assessment based on current discussion",
          category: "Risk Analysis"
        },
        {
          question: "How would this solution scale for larger implementations?",
          context: "Scalability considerations for current topic",
          category: "Scalability"
        },
        {
          question: "What metrics should I use to measure success for this initiative?",
          context: "Success measurement for current discussion topic",
          category: "Success Metrics"
        }
      ];
      
      questions = [...conversationQuestions, ...questions];
    }
    
    // Randomize and limit to 4-5 suggestions
    const shuffled = questions.sort(() => 0.5 - Math.random());
    return shuffled.slice(0, 5);
  }

  private saveQuestionHistory(): void {
    try {
      localStorage.setItem('ai-assistant-question-history', JSON.stringify(this.questionHistory));
    } catch (error) {
      console.warn('Failed to save question history to localStorage:', error);
    }
  }

  private loadQuestionHistory(): void {
    try {
      const saved = localStorage.getItem('ai-assistant-question-history');
      if (saved) {
        const parsed = JSON.parse(saved);
        this.questionHistory = parsed.map((item: any) => ({
          ...item,
          timestamp: new Date(item.timestamp)
        }));
      }
    } catch (error) {
      console.warn('Failed to load question history from localStorage:', error);
      this.questionHistory = [];
    }
  }

  // Platform Knowledge Selection Methods
  async openPageSelectionModal(): Promise<void> {
    // Only show for AI Innovator role
    if (this.selectedRole?.value !== 'ai_innovator') {
      this.messageService.add({
        severity: 'warn',
        summary: 'Feature Not Available',
        detail: 'Platform knowledge selection is only available for the AI Innovator role.'
      });
      return;
    }

    this.showPageSelectionModal = true;
    this.isLoadingPages = true;

    try {
      // Load available pages and stats
      const [pages, stats, categories] = await Promise.all([
        this.knowledgeService.getAvailablePages(),
        this.knowledgeService.getKnowledgeStats(),
        this.knowledgeService.getPagesByCategory()
      ]);

      this.availablePages = pages;
      this.knowledgeStats = stats;
      this.pagesByCategory = categories;
      this.filteredPages = [...this.availablePages];

      // Load recommendations if we have existing selections
      if (this.selectedPages.length > 0) {
        this.recommendedPages = await this.knowledgeService.getRecommendedPages(
          this.selectedPages.map(p => p.id)
        );
      } else {
        this.recommendedPages = this.availablePages.slice(0, 3);
      }

      // Store original innovator instruction for restoration if needed
      if (!this.originalInnovatorInstruction) {
        const innovatorRole = this.availableRoles.find(r => r.value === 'ai_innovator');
        this.originalInnovatorInstruction = innovatorRole?.systemInstruction || '';
      }

    } catch (error) {
      console.error('Error loading knowledge pages:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Loading Error',
        detail: 'Failed to load platform knowledge. Please try again.'
      });
    } finally {
      this.isLoadingPages = false;
      this.cdr.detectChanges();
    }
  }

  closePageSelectionModal(): void {
    this.showPageSelectionModal = false;
    this.searchTerm = '';
    this.filteredPages = [...this.availablePages];
  }

  togglePageSelection(page: SelectablePage): void {
    const isSelected = this.selectedPages.some(p => p.id === page.id);
    
    if (isSelected) {
      this.selectedPages = this.selectedPages.filter(p => p.id !== page.id);
    } else {
      if (this.selectedPages.length >= this.maxSelectedPages) {
        this.messageService.add({
          severity: 'warn',
          summary: 'Selection Limit',
          detail: `You can select up to ${this.maxSelectedPages} pages for context.`
        });
        return;
      }
      this.selectedPages.push(page);
    }

    // Update recommendations based on current selection
    this.updateRecommendations();
  }

  async updateRecommendations(): Promise<void> {
    try {
      this.recommendedPages = await this.knowledgeService.getRecommendedPages(
        this.selectedPages.map(p => p.id)
      );
    } catch (error) {
      console.error('Error updating recommendations:', error);
    }
  }

  isPageSelected(page: SelectablePage): boolean {
    return this.selectedPages.some(p => p.id === page.id);
  }

  searchPages(): void {
    if (!this.searchTerm.trim()) {
      this.filteredPages = [...this.availablePages];
      return;
    }

    this.knowledgeService.searchPages(this.searchTerm.trim()).then(results => {
      this.filteredPages = results;
      this.cdr.detectChanges();
    });
  }

  clearPageSelection(): void {
    this.selectedPages = [];
    this.updateRecommendations();
  }

  async applyPlatformContext(): Promise<void> {
    if (this.selectedPages.length === 0) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Selection',
        detail: 'Please select at least one platform page to include in the context.'
      });
      return;
    }

    this.isContextUpdating = true;
    
    try {
      // Build context for selected pages
      const platformContext = await this.knowledgeService.buildContextForPages(
        this.selectedPages.map(p => p.id)
      );

      // Update the AI Innovator role's system instruction
      const innovatorRole = this.availableRoles.find(r => r.value === 'ai_innovator');
      if (innovatorRole) {
        innovatorRole.systemInstruction = this.originalInnovatorInstruction + '\n\n' + platformContext;
        
        // If currently using AI Innovator, refresh the conversation
        if (this.selectedRole?.value === 'ai_innovator') {
          this.selectedRole = innovatorRole;
          
          // Add context update message
          const contextMessage: ChatMessage = {
            id: this.generateId(),
            role: 'assistant',
            content: `✅ **Platform Context Updated**

I now have detailed knowledge of these ${this.selectedPages.length} platform areas:
${this.selectedPages.map(p => `• **${p.name}** (${p.category})`).join('\n')}

This gives me deep insights into business logic, current limitations, and innovation opportunities across these areas. Let's explore how we can innovate within the ecosystem!

*What specific innovation challenge would you like to work on?*`,
            timestamp: new Date(),
            isUser: false
          };
          
          this.messages.push(contextMessage);
          this.scrollToBottom();
        }
      }

      this.messageService.add({
        severity: 'success',
        summary: 'Context Applied',
        detail: `Platform knowledge for ${this.selectedPages.length} page(s) has been integrated into the AI Innovator assistant.`
      });

      this.closePageSelectionModal();

    } catch (error) {
      console.error('Error applying platform context:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Context Error',
        detail: 'Failed to apply platform context. Please try again.'
      });
    } finally {
      this.isContextUpdating = false;
      this.cdr.detectChanges();
    }
  }

  removePlatformContext(): void {
    // Restore original AI Innovator instruction
    const innovatorRole = this.availableRoles.find(r => r.value === 'ai_innovator');
    if (innovatorRole && this.originalInnovatorInstruction) {
      innovatorRole.systemInstruction = this.originalInnovatorInstruction;
      
      if (this.selectedRole?.value === 'ai_innovator') {
        this.selectedRole = innovatorRole;
      }
    }

    this.selectedPages = [];
    
    this.messageService.add({
      severity: 'info',
      summary: 'Context Removed',
      detail: 'Platform knowledge context has been removed from the AI Innovator assistant.'
    });
  }

  getSelectedPagesText(): string {
    if (this.selectedPages.length === 0) {
      return 'No platform areas selected';
    }
    return `${this.selectedPages.length} platform area${this.selectedPages.length > 1 ? 's' : ''} selected`;
  }

  getSelectedPagesTokenEstimate(): number {
    return this.selectedPages.reduce((total, page) => {
      return total + (page.estimatedTokens || 1000);
    }, 0);
  }

  getComplexityIcon(complexity: string): string {
    switch (complexity) {
      case 'Simple': return 'pi-circle';
      case 'Medium': return 'pi-circle-fill';
      case 'Complex': return 'pi-circle-on';
      default: return 'pi-circle';
    }
  }

  getComplexityColor(complexity: string): string {
    switch (complexity) {
      case 'Simple': return '#10b981'; // green
      case 'Medium': return '#f59e0b'; // amber
      case 'Complex': return '#ef4444'; // red
      default: return '#6b7280'; // gray
    }
  }

  getCategoryIcon(category: string): string {
    switch (category) {
      case 'Dashboard': return 'pi-chart-line';
      case 'Patient Management': return 'pi-users';
      case 'Healthcare Workflows': return 'pi-sitemap';
      default: return 'pi-file';
    }
  }

  showPlatformKnowledgeStatus(): boolean {
    return this.selectedRole?.value === 'ai_innovator' && this.selectedPages.length > 0;
  }

  // Helper method for template access to Object.keys
  getObjectKeys(obj: any): string[] {
    return Object.keys(obj);
  }

  // Image Generation Methods
  toggleImageGenPanel(): void {
    this.showImageGenPanel = !this.showImageGenPanel;
    if (this.showImageGenPanel && !this.imagePrompt) {
      this.imagePrompt = '';
    }
  }

  closeImageGenPanel(): void {
    this.showImageGenPanel = false;
    this.generatedImageUrl = '';
    this.generatedImageData = null;
  }

  onImageModelChange(event: any): void {
    const selectedModel = this.availableImageModels.find(m => m.id === event.value);
    if (selectedModel) {
      this.selectedImageModel = selectedModel;
      this.selectedImageProvider = selectedModel.provider;
    }
  }

  async generateImage(): Promise<void> {
    if (!this.imagePrompt.trim()) {
      this.messageService.add({
        severity: 'warn',
        summary: 'Prompt Required',
        detail: 'Please enter a prompt for image generation.'
      });
      return;
    }

    this.isGeneratingImage = true;
    this.generatedImageUrl = '';
    this.generatedImageData = null;

    try {
      const options = {
        aspect: this.selectedAspectRatio,
        provider: this.selectedImageProvider
      };

      const response: any = await this.genusAIService.generateImage(
        this.imagePrompt.trim(),
        this.selectedImageProvider,
        options
      ).toPromise();

      if (response && response.data && response.data.length > 0) {
        const imageData = response.data[0];
        this.generatedImageUrl = imageData.data_uri || (imageData.b64_json ? `data:image/jpeg;base64,${imageData.b64_json}` : '');
        this.generatedImageData = imageData;

        this.messageService.add({
          severity: 'success',
          summary: 'Image Generated',
          detail: 'Your image has been generated successfully!'
        });
      } else {
        throw new Error('No image data received');
      }
    } catch (error) {
      console.error('Image generation error:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Generation Failed',
        detail: 'Failed to generate image. Please try again.'
      });
    } finally {
      this.isGeneratingImage = false;
    }
  }

  async regenerateImage(): Promise<void> {
    await this.generateImage();
  }

  insertImageInChat(): void {
    if (!this.generatedImageUrl) return;

    const imageTag = `![Generated Image](${this.generatedImageUrl})`;
    this.currentMessage = this.currentMessage ? `${this.currentMessage}\n\n${imageTag}` : imageTag;

    this.messageService.add({
      severity: 'success',
      summary: 'Image Inserted',
      detail: 'Image has been inserted into your message.'
    });
  }

  async importToLibrary(): Promise<void> {
    if (!this.generatedImageUrl || !this.generatedImageData) {
      this.messageService.add({
        severity: 'warn',
        summary: 'No Image',
        detail: 'No image available to import.'
      });
      return;
    }

    try {
      const imageMetadata = {
        url: this.generatedImageUrl,
        prompt: this.imagePrompt,
        provider: this.selectedImageProvider,
        aspectRatio: this.selectedAspectRatio,
        timestamp: new Date().toISOString(),
        category: 'AI Generated'
      };

      const existingImages = JSON.parse(localStorage.getItem('aiGeneratedImages') || '[]');
      existingImages.unshift(imageMetadata);
      
      if (existingImages.length > 50) {
        existingImages.splice(50);
      }
      
      localStorage.setItem('aiGeneratedImages', JSON.stringify(existingImages));

      this.downloadGeneratedImage();

      this.messageService.add({
        severity: 'success',
        summary: 'Image Saved',
        detail: 'Image downloaded and saved to browser storage. Use the Image Library upload to add it permanently.',
        life: 6000
      });

      this.closeImageGenPanel();
    } catch (error) {
      console.error('Error saving image:', error);
      this.messageService.add({
        severity: 'error',
        summary: 'Save Failed',
        detail: 'Failed to save image. Please try downloading instead.'
      });
    }
  }

  downloadGeneratedImage(): void {
    if (!this.generatedImageUrl) return;

    const link = document.createElement('a');
    link.href = this.generatedImageUrl;
    link.download = `ai-generated-${new Date().getTime()}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    this.messageService.add({
      severity: 'success',
      summary: 'Download Started',
      detail: 'Image download has been initiated.'
    });
  }

  private base64ToBlob(base64: string, contentType: string = ''): Blob {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    return new Blob([byteArray], { type: contentType });
  }

  supportsImageGeneration(): boolean {
    if (!this.selectedRole) return false;
    const config = (this.selectedRole as any).config;
    return config?.supportsImageGeneration === true;
  }
}